/**
 * <cart-drawer> — dialog-based mini-cart.
 *
 * Data flow (no HTML scraping):
 * - Cart state (item_count) comes from GET {data-cart-url} (/cart.js).
 * - Markup comes from the Section Rendering API: GET {data-section-url}
 *   ({root}?sections=cart-drawer) and the [data-cart-drawer-content]
 *   subtree is swapped wholesale.
 * - Line mutations POST to {data-cart-change-url} (/cart/change.js) with
 *   { id: <line key>, quantity }.
 *
 * Openers: any [data-cart-drawer-open] element (the header cart link keeps
 * its /cart href as no-JS fallback). Other scripts can dispatch
 * `document.dispatchEvent(new CustomEvent('cart:refresh', { detail: { open: true } }))`
 * after adding items (used by the PDP in a later milestone).
 */
class CartDrawer extends HTMLElement {
  connectedCallback() {
    this.dialog = this.querySelector('dialog');
    if (!this.dialog) return;

    this.dialog.addEventListener('click', (event) => {
      if (event.target === this.dialog) this.hide();
    });

    this.addEventListener('click', this.onClick.bind(this));
    this.addEventListener('change', this.onChange.bind(this));

    document.addEventListener('click', (event) => {
      const opener = event.target.closest('[data-cart-drawer-open]');
      if (!opener) return;
      event.preventDefault();
      this.show();
    });

    document.addEventListener('cart:refresh', (event) => {
      this.refresh().then(() => {
        if (event.detail?.open) this.show();
      });
    });
  }

  show() {
    if (!this.dialog.open) this.dialog.showModal();
  }

  hide() {
    this.dialog.close();
  }

  onClick(event) {
    if (event.target.closest('[data-dialog-close]')) {
      this.hide();
      return;
    }

    const removeButton = event.target.closest('[data-line-remove]');
    if (removeButton) {
      this.changeLine(removeButton.dataset.lineKey, 0);
      return;
    }

    const qtyButton = event.target.closest('[data-qty-change]');
    if (qtyButton) {
      const input = this.querySelector(`[data-line-quantity][data-line-key="${qtyButton.dataset.lineKey}"]`);
      if (!input) return;
      // A cleared/garbled input parses to NaN — step from the last rendered value instead.
      const current = Number.isNaN(Number(input.value)) || input.value.trim() === ''
        ? Number(input.defaultValue)
        : Number(input.value);
      const quantity = Math.max(0, current + Number(qtyButton.dataset.qtyChange));
      this.changeLine(qtyButton.dataset.lineKey, quantity);
    }
  }

  onChange(event) {
    const input = event.target.closest('[data-line-quantity]');
    if (!input) return;

    // Blurring an emptied/non-numeric field must not silently delete the line
    // (Number('') === 0 → quantity 0 → removal). Restore the rendered value.
    if (input.value.trim() === '' || Number.isNaN(Number(input.value))) {
      input.value = input.defaultValue;
      return;
    }

    this.changeLine(input.dataset.lineKey, Math.max(0, Number(input.value)));
  }

  async changeLine(key, quantity) {
    if (!key) return;
    this.hideError();
    this.setBusy(true);

    try {
      const response = await fetch(this.dataset.cartChangeUrl, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ id: key, quantity }),
      });

      if (!response.ok) {
        // Shopify replies 422 with { description } (e.g. stock limits).
        let message = this.dataset.errorGeneric;
        try {
          const data = await response.json();
          if (typeof data.description === 'string' && data.description) message = data.description;
        } catch {
          // Non-JSON error body — keep the generic message.
        }
        this.showError(message);
        return;
      }

      const cart = await response.json();
      await this.refresh();

      // Stock cap: Shopify answers 200 but returns a lower quantity than
      // requested when all available stock is already in the cart.
      const line = cart.items.find((item) => item.key === key);
      if (line && quantity > line.quantity) this.showError(this.dataset.errorStock);
    } catch (error) {
      console.error(error);
      this.showError(this.dataset.errorGeneric);
    } finally {
      this.setBusy(false);
    }
  }

  /** Never rejects — failures surface in the [data-cart-error] region. */
  async refresh() {
    try {
      const [cart, sectionHtml] = await Promise.all([
        fetch(this.dataset.cartUrl, { headers: { Accept: 'application/json' } }).then((response) => response.json()),
        fetch(this.dataset.sectionUrl)
          .then((response) => response.json())
          .then((sections) => sections['cart-drawer']),
      ]);

      if (sectionHtml) {
        const parsed = new DOMParser().parseFromString(sectionHtml, 'text/html');
        const newContent = parsed.querySelector('[data-cart-drawer-content]');
        const currentContent = this.querySelector('[data-cart-drawer-content]');
        if (newContent && currentContent) currentContent.replaceWith(newContent);
      }

      document.querySelectorAll('[data-cart-count]').forEach((element) => {
        element.textContent = cart.item_count;
        element.classList.toggle('hidden', cart.item_count === 0);
      });
    } catch (error) {
      console.error(error);
      this.showError(this.dataset.errorGeneric);
    }
  }

  showError(message) {
    const region = this.querySelector('[data-cart-error]');
    if (!region) return;
    region.textContent = message || this.dataset.errorGeneric;
    region.classList.remove('hidden');
  }

  hideError() {
    const region = this.querySelector('[data-cart-error]');
    if (!region) return;
    region.textContent = '';
    region.classList.add('hidden');
  }

  setBusy(busy) {
    const content = this.querySelector('[data-cart-drawer-content]');
    content?.setAttribute('aria-busy', busy ? 'true' : 'false');
    if (content) content.style.opacity = busy ? '0.5' : '';
  }
}

if (!customElements.get('cart-drawer')) {
  customElements.define('cart-drawer', CartDrawer);
}
